import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import 'bootstrap/dist/css/bootstrap.css';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Tooltip,
  Button,
  Divider,
  CardActions,
  TablePagination,
  Typography,
  CircularProgress
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Done, GetApp } from '@material-ui/icons';
import { SortButton, FilterBar } from 'components';
import SurveyResultResponseModal from './components';
import {
  closeSentResponseSnackbar,
  getGuestDetails,
  getIndividualSurveyResults,
  unsetSurveySettingsData,
  getSurveyResultCsvFile,
  downloadedSurveyResultCsvFile,
  updateEventIdWithoutEffects,
  updateIndividualSurveyResultFilters,
  resetIndividualSurveyResultFilters
} from 'store/surveys/actions';
import { actions } from 'store/surveyTemplate';
import { actions as surveyActions } from 'store/surveys';
import UserMessageSnackbar from 'views/UserMessaging/customModalContent/userMessageSnackbar';
import DisplayModal from 'components/DisplayModal';
import moment from 'moment';
import { getEnvVar } from 'helpers';
import { excludeSurveyTools } from 'constants.js';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/defaultV2.min.css';

const useStyles = makeStyles((theme) => ({
  root: {},
  cardHeader: {},
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%'
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    width: '95%'
  },
  closeIcon: {
    margin: '5px',
    marginTop: '24px'
  },
  actions: {
    justifyContent: 'flex-end'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20%'
  },
  cardButtons: {
    float: 'right',
    marginTop: '12px',
    marginBottom: '12px',
    marginRight: '16px'
  },
  noResultsDiv: {
    textAlign: 'center',
    margin: '16px',
    marginTop: '40px',
    fontSize: '20px'
  },
  overflowDiv: {
    maxHeight: '300px',
    overflowY: 'auto'
  },
  filterDiv: {
    padding: theme.spacing(2),
    backgroundColor: '#f1f1f1'
  },
  innerDiv: {
    display: 'flex',
    paddingRight: '35px',
    width: '50%'
  },
  actionButtons: {
    display: 'flex'
  },
  exportButton: {
    display: 'flex'
  },
  downloadGrid: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  surveyInfoPanel: {
    display: 'flex',
    '& div': {
      width: '100%'
    }
  }
}));

const IndividualResults = (props) => {
  const { className, staticContext, survey, back, showNav, ...rest } = props;
  const {
    thisSurvey,
    isSurveyLoading,
    closeSurvey,
    thisIndividualSurveyResults,
    setCurrentTemplateDetails,
    updateCurrentErrorValidationSegment,
    closeSurveySettings,
    setSurveyResultData
  } = survey;
  const classes = useStyles();
  const dispatch = useDispatch();
  const surveyResultRoots = useRef({});
  const { surveyHash, eventIdParam, campaignUuidParam } = useParams();
  const admExcludeSurveyTools = getEnvVar('ADM_EXCLUDE_SURVEY_TOOLS')?.split(
    ','
  ) || [...excludeSurveyTools];
  const optionsToSort = [
    { name: 'createTime', value: 'Most Recent', checked: false }
  ];
  const sortTypeOptions = [
    { name: 'ASC', value: 'Ascending', checked: false },
    { name: 'DESC', value: 'Descending', checked: false }
  ];
  const [sortOptionsAdded, setSortOptionsAdded] = useState([...optionsToSort]);
  const [sortTypes, setSortTypes] = useState([...sortTypeOptions]);
  const [campaignStatusFilter, setCampaignStatusFilter] = useState('all');
  const [showSurveyResponseModal, setShowSurveyResponseModal] = useState(false);
  const [showTemplateCreateModal, setShowTemplateCreateModal] = useState(false);
  const [snackbarInfo, setSnackBarInfo] = useState({ show: false });
  let [surveyQuestions, setSurveyQuestions] = useState([]);

  const gettingFileSucceeded = useSelector(
    (store) => store.survey.surveyResultCsv.gettingFileSucceeded
  );
  const gettingFile = useSelector(
    (store) => store.survey.surveyResultCsv.gettingFile
  );
  const isDownloading = useSelector(
    (store) => store.survey.surveyResultCsv.isDownloading
  );
  const surveyResultCsvBlob = useSelector(
    (store) => store.survey.surveyResultCsv.surveyResultCsvBlob
  );

  useEffect(() => {
    return () => {
      Object.values(surveyResultRoots.current).forEach((root) =>
        root.unmount()
      );
      surveyResultRoots.current = {};
    };
  }, []);

  useEffect(() => {
    if (!gettingFile) {
      if (gettingFileSucceeded && surveyResultCsvBlob.type === 'text/csv') {
        const url = window.URL.createObjectURL(new Blob([surveyResultCsvBlob]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${thisSurvey.name}.csv`;
        link.click();
      }
      dispatch(downloadedSurveyResultCsvFile());
    }
  }, [gettingFileSucceeded]);

  const sendResponseSuccess = useSelector(
    (store) => store.survey.sendResponseSuccess
  );
  const openSentResponseSnackbar = useSelector(
    (store) => store.survey.openSentResponseSnackbar
  );
  const filters = useSelector(
    (store) => store.survey.individualSurveyResultFilters
  );

  const associatedCampaigns = useSelector(
    (store) => store.survey.associatedCampaignList
  );
  const gettingGuestDetails = useSelector(
    (store) => store.survey.gettingGuestDetails
  );
  const guestDetails = useSelector((store) => store.survey.guestDetails);

  const optionsToFilterType = [
    { name: 'type', value: 'ACKNOWLEDGED', checked: false },
    { name: 'type', value: 'NOT RESPONDED', checked: false }
  ];
  const optionsToFilterTags = associatedCampaigns
    ? associatedCampaigns.map((associatedCampaign) => {
        return {
          name: 'campaigns',
          displayName: associatedCampaign.name,
          value: associatedCampaign.uuid,
          checked: false,
          active: associatedCampaign.active
        };
      })
    : [];

  const optionsToFilterSurveyQuestions = surveyQuestions.map((option) => {
    let modifiedQuestion = { ...option };
    if (option.type === 'matrix') {
      modifiedQuestion.rows = modifiedQuestion.rows.map((row) => {
        return {
          name: row,
          regex: ''
        };
      });
      return { ...modifiedQuestion, checked: false };
    } else {
      return { ...modifiedQuestion, checked: false, regex: '' };
    }
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentGuest, setcurrentGuest] = useState({});
  const [emptyMessage, setEmptyMessage] = React.useState({
    show: false,
    msg: 'No Event Tracking info available.'
  });

  const [isScoresModalOpen, setIsScoresModalOpen] = useState(false);
  const [currentScores, setcurrentScores] = useState({});

  useEffect(() => {
    updateCurrentErrorValidationSegment('sendResponseData');
    setCurrentTemplateDetails();
    dispatch(surveyActions.getAssociatedCampaigns({ surveyHash }));
    dispatch(actions.getSurveyTemplates({ force: true }));
  }, []);

  useEffect(() => {
    if (thisSurvey.details) {
      let questionArray = JSON.parse(thisSurvey.details).pages.reduce(
        (acc, page) => {
          page.elements.forEach((element) => {
            if (!admExcludeSurveyTools.includes(element.type)) {
              acc.push({ ...element });
            }
          });
          return acc;
        },
        []
      );
      setSurveyQuestions(questionArray || []);
    }
  }, [thisSurvey.details]);

  useEffect(() => {
    if (openSentResponseSnackbar) {
      if (sendResponseSuccess) {
        setSnackBarInfo({
          show: true,
          message: 'Sent response successfully.',
          severity: 'success'
        });
      } else {
        setSnackBarInfo({
          show: true,
          message: 'Failed to send response.',
          severity: 'error'
        });
      }
    } else {
      setSnackBarInfo({
        show: false,
        message: ''
      });
    }
  }, [openSentResponseSnackbar]);

  useEffect(() => {
    if (sendResponseSuccess) {
      closeCreateTemplateDialog();
      closeSendResponseModal();
    }
  }, [sendResponseSuccess]);

  useEffect(() => {
    dispatch(resetIndividualSurveyResultFilters());
    if (campaignUuidParam) {
      dispatch(
        updateIndividualSurveyResultFilters({
          filterTags: [campaignUuidParam],
          pageNo: 0
        })
      );
    }
    if (eventIdParam) {
      dispatch(updateEventIdWithoutEffects(eventIdParam));
    }
    dispatch(getIndividualSurveyResults({ surveyHash }));
  }, []);

  useEffect(() => {
    if (!isSurveyLoading) {
      const activeRootIds = [];
      let questionnaire = thisSurvey?.details && JSON.parse(thisSurvey.details);
      let results = thisIndividualSurveyResults || [];
      if (results.length > 0) {
        results
          .map((r) => JSON.parse(r.results))
          .map((result, index) => {
            const survey = new Model(questionnaire);
            survey.data = result;
            survey.mode = 'display';
            const surveyElementId = 'surveyElement' + index;
            const surveyElement = document.getElementById(surveyElementId);
            if (surveyElement) {
              if (!surveyResultRoots.current[surveyElementId]) {
                surveyResultRoots.current[surveyElementId] =
                  createRoot(surveyElement);
              }
              surveyResultRoots.current[surveyElementId].render(
                <Survey model={survey} />
              );
              activeRootIds.push(surveyElementId);
            }
          });
      }
      Object.keys(surveyResultRoots.current).forEach((surveyElementId) => {
        if (!activeRootIds.includes(surveyElementId)) {
          surveyResultRoots.current[surveyElementId].unmount();
          delete surveyResultRoots.current[surveyElementId];
        }
      });
    }
  }, [thisSurvey, thisIndividualSurveyResults]);

  useEffect(() => {
    if (!gettingGuestDetails) {
      setcurrentGuest(guestDetails);
      setEmptyMessage({
        ...emptyMessage,
        show: !Object.keys(guestDetails).length > 0
      });
    }
  }, [gettingGuestDetails, guestDetails]);

  const getFilteredCampaignOptions = () => {
    if (campaignStatusFilter === 'all') {
      return optionsToFilterTags;
    } else if (campaignStatusFilter === 'active') {
      return optionsToFilterTags.filter((option) => option.active === true);
    } else if (campaignStatusFilter === 'archived') {
      return optionsToFilterTags.filter((option) => option.active === false);
    }
    return optionsToFilterTags;
  };

  const handleCampaignStatusChange = (newStatus) => {
    setCampaignStatusFilter(newStatus);
  };

  const getSurveyQuestionsList = () => {
    return filters?.filterSurveyQuestions
      ?.filter((option) => option.checked)
      .map((option) => option.name);
  };

  const filterOptions = {
    filters: [
      {
        options: optionsToFilterType.map((option) => {
          if (filters?.filterTypes?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Response Type',
        values: filters?.filterTypes,
        onChange: (types) =>
          dispatch(
            updateIndividualSurveyResultFilters({
              filterTypes: types,
              pageNo: 0
            })
          )
      }
    ],
    autocompleteSearch: {
      options: getFilteredCampaignOptions().map((option) => {
        if (filters?.filterTags?.includes(option.value)) {
          option.checked = true;
        }
        return option;
      }),
      title: 'Campaigns',
      values: filters?.filterTags,
      onChange: (tags) =>
        dispatch(
          updateIndividualSurveyResultFilters({ filterTags: tags, pageNo: 0 })
        ),
      showStatusFilter: true,
      statusFilter: campaignStatusFilter,
      onStatusChange: handleCampaignStatusChange
    },
    dateRangeFilters: {
      startDate: {
        title: 'From Date',
        keyName: 'startDate',
        format: 'YYYY-MM-DD',
        value: filters?.filterStartDate,
        maxDate: moment(),
        action: (startDate) =>
          dispatch(
            updateIndividualSurveyResultFilters({
              filterStartDate: startDate,
              pageNo: 0
            })
          )
      },
      endDate: {
        title: 'To Date',
        keyName: 'endDate',
        format: 'YYYY-MM-DD',
        value: filters?.filterEndDate,
        minDate: filters?.filterStartDate ? filters?.filterStartDate : moment(),
        maxDate: moment(),
        action: (endDate) =>
          dispatch(
            updateIndividualSurveyResultFilters({
              filterEndDate: endDate,
              pageNo: 0
            })
          )
      }
    },
    queryFilters: {
      options:
        optionsToFilterSurveyQuestions.map((option) => {
          if (getSurveyQuestionsList()?.includes(option.name)) {
            option.checked = true;
          }
          return option;
        }) || [],
      title: 'Survey Questions',
      intraDependency: filters?.filterSurveyQuestions,
      onChange: (updatedSurveyQuestion) =>
        dispatch(
          updateIndividualSurveyResultFilters({
            filterSurveyQuestions: updatedSurveyQuestion,
            pageNo: 0
          })
        )
    },
    storeSelector: (store) => store.survey.individualSurveyResultFilters,
    resetFilters: () => dispatch(resetIndividualSurveyResultFilters())
  };

  const openCreateTemplateDialog = (templateId) => {
    if (!templateId) {
      dispatch(actions.unsetNextStep());
      setCurrentTemplateDetails();
      dispatch(
        actions.getSurveyTemplate({ productType: thisSurvey.productType })
      );
      setShowTemplateCreateModal(true);
    } else {
      setCurrentTemplateDetails();
      setShowTemplateCreateModal(true);
    }
  };

  const closeCreateTemplateDialog = () => {
    setShowTemplateCreateModal(false);
    dispatch(actions.unsetNextStep());
  };

  const handlePageChange = (event, page) => {
    dispatch(updateIndividualSurveyResultFilters({ pageNo: page }));
    dispatch(getIndividualSurveyResults({ surveyHash }));
  };

  const handleRowsPerPageChange = (event) => {
    dispatch(
      updateIndividualSurveyResultFilters({
        rowsPerPage: event.target.value,
        pageNo: 0
      })
    );
    dispatch(updateIndividualSurveyResultFilters({ pageNo: 0 }));
    dispatch(getIndividualSurveyResults({ surveyHash }));
  };

  const getSortByOption = () => {
    if (sortOptionsAdded.filter((option) => option.checked).length === 1) {
      return sortOptionsAdded.filter((option) => option.checked)[0].name;
    } else {
      return null;
    }
  };

  const getOrderByOption = () => {
    if (sortTypes.filter((option) => option.checked).length === 1) {
      return getSortByOption() === 'createTime'
        ? sortTypes.filter((option) => !option.checked)[0].name
        : sortTypes.filter((option) => option.checked)[0].name;
    } else {
      return null;
    }
  };

  const openSendResponseModal = (result) => {
    setShowSurveyResponseModal(true);
    setSurveyResultData(result);
  };

  const closeSendResponseModal = () => {
    setShowSurveyResponseModal(false);
    closeSurveySettings();
    dispatch(unsetSurveySettingsData());
  };

  const onSortOptionChange = (options) => {
    setSortOptionsAdded(options);
    if (getOrderByOption() === null) {
      setSortTypes(
        sortTypes.map((option) => {
          if (option.name === 'ASC') {
            return { ...option, checked: true };
          } else {
            return { ...option };
          }
        })
      );
    }
    if (options[0].checked) {
      dispatch(
        updateIndividualSurveyResultFilters({
          sortBy: 'createTime',
          sortOrder: 'ASC',
          pageNo: 0
        })
      );
    } else {
      dispatch(updateIndividualSurveyResultFilters({ sortBy: '', pageNo: 0 }));
      setSortTypes([...sortTypeOptions]);
    }
    dispatch(getIndividualSurveyResults({ surveyHash }));
  };

  const onSortTypeChange = (options) => {
    let order = options.filter((option) => option.checked);
    if (order.length === 0) {
      options = options.map((option) => {
        if (option.name === 'ASC') {
          order = [{ ...option, checked: true }];
          return { ...option, checked: true };
        }
        return option;
      });
    }
    setSortTypes(options);
    dispatch(
      updateIndividualSurveyResultFilters({
        sortOrder: order[0].name,
        pageNo: 0
      })
    );
    dispatch(getIndividualSurveyResults({ surveyHash }));
  };

  const onCloseSnackbar = () => {
    dispatch(closeSentResponseSnackbar());
  };

  const closeIndividualSurvey = () => {
    closeSurvey();
  };

  const onGuestDetailsClick = (result) => {
    dispatch(getGuestDetails(result));
    setIsModalOpen((prev) => !prev);
  };

  const onViewScoresClick = (result) => {
    let scoreMap = {};
    let metrics = {};
    thisSurvey.postProcessing.promoterScores.map(
      (score) => (scoreMap[score.uuid] = score.scoreName)
    );
    result.metrics.promoterScores.map((metric) => {
      metrics[scoreMap[metric.uuid]] = metric.score;
    });
    setcurrentScores(metrics);
    setIsScoresModalOpen((prev) => !prev);
  };

  const onClickDownloadCsv = () => {
    dispatch(getSurveyResultCsvFile(thisSurvey.surveyHash));
  };

  return (
    <div className={classes.root}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          <div className={classes.headerContainer}>
            <div className={classes.title}>
              <CardHeader
                title="Individual Survey Results"
                titleTypographyProps={{ variant: 'h1' }}
              />
            </div>
            <div className={classes.closeIcon}>
              <Tooltip title="Close" arrow>
                <CloseIcon onClick={closeIndividualSurvey} color="error" />
              </Tooltip>
            </div>
          </div>
          <CardContent>
            <Card className={classes.filterDiv}>
              <div className={classes.surveyInfoPanel}>
                <div className={classes.innerDiv}>
                  <TextField
                    fullWidth
                    style={{ marginTop: '12px' }}
                    label="Survey Name"
                    margin="dense"
                    name="name"
                    value={thisSurvey.name || ''}
                    required
                    variant="outlined"
                    disabled={true}
                  />
                </div>
                <div className={classes.actionButtons}>
                  <div>
                    <SortButton
                      options={sortOptionsAdded}
                      sortTypes={sortTypes}
                      onChangeSortOption={onSortOptionChange}
                      onChangeSortType={onSortTypeChange}
                    />
                  </div>
                  {thisIndividualSurveyResults?.length > 0 && (
                    <div className={classes.downloadGrid}>
                      <Button
                        onClick={onClickDownloadCsv}
                        startIcon={<GetApp />}
                        disabled={isDownloading}
                        color="primary"
                        variant="outlined"
                      >
                        Export
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div className={classes.surveyFilterPanel}>
                {filterOptions && (
                  <FilterBar
                    filterOptions={filterOptions}
                    action={getIndividualSurveyResults({ surveyHash })}
                  ></FilterBar>
                )}
              </div>
            </Card>
            {isSurveyLoading && (
              <div className={classes.loader}>
                <CircularProgress />
              </div>
            )}
            {!isSurveyLoading &&
              thisIndividualSurveyResults &&
              thisIndividualSurveyResults.length > 0 &&
              thisIndividualSurveyResults.map((result, index) => {
                return (
                  <React.Fragment key={'resultKey' + index}>
                    <Card
                      key={'resultKey' + index}
                      style={{ marginTop: '24px' }}
                    >
                      <CardContent className={classes.overflowDiv}>
                        <div id={'surveyElement' + index}></div>
                        <div id={'surveyResult' + index}></div>
                      </CardContent>
                      <Divider />
                      <Button
                        className={classes.cardButtons}
                        color="primary"
                        variant="contained"
                        disabled={!result.metrics?.promoterScores?.length > 0}
                        onClick={() => onViewScoresClick(result)}
                      >
                        {`View scores`}
                      </Button>
                      <Button
                        className={classes.cardButtons}
                        color="primary"
                        variant="contained"
                        disabled={isSurveyLoading}
                        onClick={() => onGuestDetailsClick(result)}
                      >
                        {`View ${
                          thisSurvey.productType === 'LIST'
                            ? `Profile`
                            : `Guest`
                        } Details`}
                      </Button>
                      {result.acknowledged ? (
                        <Button
                          className={classes.cardButtons}
                          style={{ pointerEvents: 'none' }}
                          color="primary"
                          id="acknowledged_btn"
                          startIcon={<Done />}
                        >
                          Acknowledged
                        </Button>
                      ) : (
                        <Button
                          className={classes.cardButtons}
                          color="primary"
                          variant="contained"
                          disabled={isSurveyLoading}
                          onClick={() => {
                            openSendResponseModal(result);
                          }}
                        >
                          Respond
                        </Button>
                      )}
                    </Card>
                  </React.Fragment>
                );
              })}
            {!isSurveyLoading && thisIndividualSurveyResults?.length === 0 && (
              <Typography className={classes.noResultsDiv}>
                No survey results.
              </Typography>
            )}
          </CardContent>
        </form>
        <CardActions className={classes.actions}>
          <TablePagination
            component="div"
            count={filters?.totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            page={filters?.pageNo}
            rowsPerPage={filters?.rowsPerPage || 10}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </CardActions>
      </Card>

      {showSurveyResponseModal && (
        <SurveyResultResponseModal
          openCreateTemplateDialog={openCreateTemplateDialog}
          closeCreateTemplateDialog={closeCreateTemplateDialog}
          showTemplateCreateModal={showTemplateCreateModal}
          survey={survey}
          showSurveyResponseModal={showSurveyResponseModal}
          closeSendResponseModal={closeSendResponseModal}
        />
      )}

      {
        <UserMessageSnackbar
          onClose={onCloseSnackbar}
          openSnackbar={snackbarInfo.show}
          message={snackbarInfo.message}
          severity={snackbarInfo.severity}
        ></UserMessageSnackbar>
      }
      <DisplayModal
        batch={currentGuest}
        title={`${
          thisSurvey.productType === 'LIST' ? `Profile` : `Guest`
        } Details`}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        isLoading={gettingGuestDetails}
        emptyMessage={emptyMessage}
        isConversionRequired={true}
        isIndentationRequired={true}
        size={'md'}
      />
      <DisplayModal
        batch={currentScores}
        title={`Promoter Scores`}
        isOpen={isScoresModalOpen}
        setIsOpen={setIsScoresModalOpen}
        size={'xs'}
      />
    </div>
  );
};

export default IndividualResults;
